<script setup>
import { ref } from 'vue';
import { Field, useForm } from 'vee-validate';
import { useToast } from 'primevue/usetoast';
import { formatarData } from "@/helpers/date";
import * as yup from 'yup';
import CertidaoService from '@/services/CertidaoService';
import Button from 'primevue/button';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';

const emit = defineEmits(['loading', 'resultado']);
const toast = useToast();

const validando = ref(false);
const resultadoValidacao = ref(null);

const schemaValidacao = yup.object({
    codigo: yup.string().required('O código da certidão é obrigatório.')
});

const { handleSubmit, resetForm } = useForm({
    validationSchema: schemaValidacao,
    initialValues: { codigo: '' }
});

const exibirValor = (value) => {
    return value === null || value === undefined || value === '' ? 'Não informado' : String(value);
}

const handleApiError = (e, fallbackMsg) => {
    const validationErrors = e.response?.data?.errors;
    const errorMessage = e.response?.data?.message;

    if (validationErrors) {
        const messages = Object.values(validationErrors).flat().join('<br/>');

        toast.add({ severity: 'info', summary: 'Atenção', detail: messages, life: 5000 });

        return;
    }

    if (errorMessage) {
        toast.add({ severity: 'info', summary: 'Atenção', detail: errorMessage, life: 5000 });

        return;
    }

    toast.add({ severity: 'warn', summary: 'Atenção', detail: fallbackMsg, life: 5000 });
}

const submitValidacao = handleSubmit(async (values) => {
    const codigo = String(values.codigo || '').trim();

    if (!codigo) {
        toast.add({
            severity: 'warn',
            summary: 'Atenção',
            detail: 'Código da certidão não informado.',
            life: 3000
        });

        return;
    }

    validando.value = true;
    resultadoValidacao.value = null;

    try {
        const resp = await CertidaoService.validar(codigo);

        resultadoValidacao.value = resp.data?.data ?? resp.data ?? null;

        emit('resultado', resultadoValidacao.value);
    } catch (e) {
        handleApiError(e, 'Não foi possível validar a certidão fiscal.');
    } finally {
        validando.value = false;
    }
});

const limparValidacao = () => {
    resetForm();

    resultadoValidacao.value = null;

    emit('resultado', null);
}
</script>

<template>
    <Card class="consulta-card">
        <template #title>
            <div class="card-header">
                <h2>Validação de certidão</h2>
            </div>
        </template>

        <template #content>
            <form @submit="submitValidacao" class="d-flex flex-column gap-2">
                <div class="field">
                    <label for="codigo">Código da certidão</label>

                    <Field name="codigo" v-slot="{ field, errorMessage }">
                        <InputText
                            v-bind="field"
                            id="codigo"
                            placeholder="Digite o código de validação"
                            maxlength="80"
                            class="w-full"
                        />

                        <small v-if="errorMessage" class="text-danger">{{ errorMessage }}</small>
                    </Field>
                </div>

                <div class="d-flex gap-2">
                    <Button
                        label="Validar"
                        icon="pi pi-check"
                        severity="primary"
                        type="submit"
                        :loading="validando"
                        :disabled="validando"
                    />

                    <Button
                        label="Limpar"
                        icon="pi pi-eraser"
                        severity="secondary"
                        outlined
                        type="button"
                        @click="limparValidacao"
                        :disabled="validando"
                    />
                </div>
            </form>

            <!-- RESULTADO VALIDACAO -->
            <div v-if="resultadoValidacao" class="resultado-validacao">
                <div class="resultado-header">
                    <div>
                        <span class="resultado-label">Resultado da validação</span>

                        <h2>{{ exibirValor(resultadoValidacao.tipocertidao) }}</h2>
                    </div>

                    <Tag
                        :value="resultadoValidacao.vencida ? 'Vencida' : 'Válida'"
                        :severity="resultadoValidacao.vencida ? 'danger' : 'success'"
                    />
                </div>

                <dl class="dados-grid">
                    <div>
                        <dt>Instituição</dt>
                        <dd>{{ exibirValor(resultadoValidacao.instituicao) }}</dd>
                    </div>

                    <div>
                        <dt>Nome</dt>
                        <dd>{{ exibirValor(resultadoValidacao.nome) }}</dd>
                    </div>

                    <div>
                        <dt>CGM</dt>
                        <dd>{{ exibirValor(resultadoValidacao.cgm) }}</dd>
                    </div>

                    <div>
                        <dt>Matrícula</dt>
                        <dd>{{ exibirValor(resultadoValidacao.matric) }}</dd>
                    </div>

                    <div>
                        <dt>Inscrição</dt>
                        <dd>{{ exibirValor(resultadoValidacao.inscr) }}</dd>
                    </div>

                    <div>
                        <dt>Geração</dt>
                        <dd>{{ formatarData(resultadoValidacao.geracao, false) }}</dd>
                    </div>

                    <div>
                        <dt>Vencimento</dt>
                        <dd>{{ formatarData(resultadoValidacao.venc, false) }}</dd>
                    </div>
                </dl>
            </div>
        </template>
    </Card>
</template>

<style scoped>
.card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 0;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field label {
    font-weight: 500;
    font-size: 0.875rem;
    color: #334155;
}

.actions {
    display: flex;
    gap: 12px;
}

.resultado-validacao {
    margin-top: 24px;
    padding: 20px;
    background: var(--color-primary-light);
    border-radius: 8px;
}

.resultado-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
}

.resultado-header h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 4px 0 0 0;
}

.resultado-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.dados-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.dados-grid div {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.dados-grid dt {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.dados-grid dd {
    font-size: 0.95rem;
    font-weight: 500;
    color: #0f172a;
    margin: 0;
}
</style>
